import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';
import { FieldDef, FieldGroup } from '../config/modules.config';

type ColumnDef = { column: string; nullable: boolean };
type SchemaMap  = Record<string, Record<string, ColumnDef[]>>;

const MODULE_COLORS: Record<string, string> = {
  portfolio:                '#232459',
  initiative:               '#1a85d6',
  risk:                     '#1a7a6a',
  okr:                      '#2a9d87',
  delivery_obligations:     '#7c3aed',
  metrics:                  '#0891b2',
  vendor_master:            '#b45309',
  tech_map_applications:    '#0f766e',
  tech_map_infrastructures: '#0f766e',
  tech_map_services:        '#0f766e',
  workforce_catalog:        '#be185d',
  people_competency_map:    '#7c3aed',
  profit_cost_centres:      '#15803d',
  tbm_cost_pool:            '#b45309',
  tbm_it_tower:             '#0f766e',
  tbm_service:              '#7c3aed',
  tbm_solution:             '#1a7a6a',
  module_owners:            '#7c3aed',
  catalog_owners:           '#0891b2',
};

function toLabel(name: string): string {
  return name
    .replace(/^(base_|delivery_|opportunity_|ipdb_metrics_|master_|term_)/, '')
    .split('_')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

const TABLE_LABEL_OVERRIDES: Record<string, string> = {
  delivery_riskdomain:      'Risk Domain',
  delivery_risksubdomain:   'Risk Subdomain',
  delivery_masterriskcontrol: 'Risk Control',
};

/**
 * Display names for tables whose derived label reads poorly — the generic
 * rule can't split run-together names, so `delivery_masterriskcontrol`
 * would otherwise surface as "Masterriskcontrol".
 */
const TABLE_LABELS: Record<string, string> = {
  delivery_masterriskcontrol:     'Risk Controls',
  delivery_masterriskmitigations: 'Risk Mitigations',
};

function tableLabel(table: string): string {
  const override = TABLE_LABELS[table];
  if (override) return override;

  return table
    .replace(/^(base_|delivery_|opportunity_|ipdb_metrics_|master_|term_)/, '')
    .split(/(?=[A-Z])|_/)
    .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

@Injectable({ providedIn: 'root' })
export class OnboardingSchemaService {
  private http   = inject(HttpClient);
  private auth   = inject(AuthService);
  private schema: SchemaMap = {};
  private loaded = false;

  async load(): Promise<void> {
    if (this.loaded) return;
    const { token } = await this.auth.ensureAuth();
    this.schema = await firstValueFrom(
      this.http.get<SchemaMap>(`${environment.keplerUrl}/onboarding-schema/`, {
        headers: new HttpHeaders({ Authorization: `JWT ${token}` }),
      })
    );
    this.loaded = true;
  }

  /**
   * Merge strategy:
   * 1. Start with ALL static fields — their semantic IDs are preserved so the
   *    mapping service (which hardcodes those IDs) keeps working.
   * 2. Resolve each static field's `optional` flag from the DB:
   *    a. Explicit match: field has dbTable + dbColumn → look up nullable directly.
   *    b. ID match: field id equals a column name in any table → use that nullable.
   *    c. No match (synthetic fields like `linked_program`) → keep static value.
   * 3. Append any extra DB columns that have no static match as additional
   *    optional fields — giving users the ability to map them too.
   */
  getFields(module: string, staticFields: FieldDef[]): FieldDef[] {
    if (!this.loaded) return staticFields;

    const moduleSchema = this.schema[module] ?? {};
    const color = MODULE_COLORS[module] ?? '#07b797';

    // Build a nullable lookup: "table::column" → nullable boolean
    const nullableByDb: Record<string, boolean> = {};
    // Also build a column-name lookup for id-based matching
    const nullableByColumn: Record<string, boolean> = {};
    for (const [table, columns] of Object.entries(moduleSchema)) {
      for (const col of columns) {
        nullableByDb[`${table}::${col.column}`] = col.nullable;
        // Last-write wins for same column name across tables (should be consistent)
        nullableByColumn[col.column] = col.nullable;
      }
    }

    const coveredDbKeys = new Set<string>();
    const staticFieldIds = new Set(staticFields.map(f => f.id));

    // Step 1 & 2 — static fields first, optional resolved from DB
    // Exception: if the static field has optional explicitly pinned (true/false),
    // that value is authoritative and the DB is not allowed to override it.
    const merged: FieldDef[] = staticFields.map(f => {
      if (f.dbTable && f.dbColumn) {
        // Explicit mapping — most reliable
        const key = `${f.dbTable}::${f.dbColumn}`;
        coveredDbKeys.add(key);
        if (f.optional !== undefined) return f; // pinned — don't touch
        const nullable = nullableByDb[key];
        return nullable !== undefined ? { ...f, optional: nullable } : f;
      }
      // ID-based fallback — field id matches a real column name
      if (f.optional === undefined && f.id in nullableByColumn) {
        return { ...f, optional: nullableByColumn[f.id] };
      }
      return f; // pinned or synthetic — keep as-is
    });

    // Step 3 — append extra DB columns not already covered
    for (const [table, columns] of Object.entries(moduleSchema)) {
      for (const col of columns) {
        const key = `${table}::${col.column}`;
        // Also skip if any static field's id matches this column name directly
        if (coveredDbKeys.has(key) || staticFieldIds.has(col.column)) continue;
        merged.push({
            id:       col.column,
            label:    toLabel(col.column),
            color,
            kw:       [col.column.replace(/_/g, ' ')],
            optional: col.nullable,
            dbTable:  table,
            dbColumn: col.column,
          });
        }
      }

    return merged.filter(f => !f.hidden);
  }

  /**
   * Same merge strategy as getFields but grouped by DB table.
   * Used when a module writes to more than one table.
   */
  getFieldGroups(module: string, staticFields: FieldDef[]): FieldGroup[] | undefined {
    if (!this.loaded) return undefined;

    const moduleSchema = this.schema[module] ?? {};
    const tables = Object.keys(moduleSchema);
    if (tables.length <= 1) return undefined;

    const color = MODULE_COLORS[module] ?? '#07b797';

    const nullableByDb: Record<string, boolean> = {};
    const nullableByColumn: Record<string, boolean> = {};
    for (const [table, columns] of Object.entries(moduleSchema)) {
      for (const col of columns) {
        nullableByDb[`${table}::${col.column}`] = col.nullable;
        nullableByColumn[col.column] = col.nullable;
      }
    }

    // Group static fields by their dbTable
    const staticByTable: Record<string, FieldDef[]> = {};
    const staticNoTable: FieldDef[] = [];

    for (const f of staticFields) {
      if (f.dbTable) {
        (staticByTable[f.dbTable] ??= []).push(f);
      } else {
        staticNoTable.push(f);
      }
    }

    const staticFieldIds = new Set(staticFields.map(f => f.id));

    const groups: FieldGroup[] = tables.map(table => {
      const coveredCols = new Set<string>();
      const fields: FieldDef[] = [];

      // Static fields for this table first — optional resolved from DB
      // (skipped when static field has optional explicitly pinned)
      for (const f of (staticByTable[table] ?? [])) {
        if (f.dbColumn) coveredCols.add(f.dbColumn);
        if (f.optional !== undefined) { fields.push(f); continue; }
        const key = `${table}::${f.dbColumn}`;
        const nullable = f.dbColumn ? nullableByDb[key] : nullableByColumn[f.id];
        fields.push(nullable !== undefined ? { ...f, optional: nullable } : f);
      }

      // Extra DB columns not covered by static
      for (const col of (moduleSchema[table] ?? [])) {
        if (!coveredCols.has(col.column) && !staticFieldIds.has(col.column)) {
          fields.push({
            id:       col.column,
            label:    toLabel(col.column),
            color,
            kw:       [col.column.replace(/_/g, ' ')],
            optional: col.nullable,
            dbTable:  table,
            dbColumn: col.column,
          });
        }
      }

      return { label: tableLabel(table), fields: fields.filter(f => !f.hidden) };
    });

    // Static fields with no dbTable — optional resolved from DB by id match
    // (skipped when optional is explicitly pinned)
    if (staticNoTable.length) {
      groups[0].fields.unshift(...staticNoTable.map(f => {
        if (f.optional !== undefined) return f;
        const nullable = nullableByColumn[f.id];
        return nullable !== undefined ? { ...f, optional: nullable } : f;
      }));
    }

    return groups;
  }
}
